import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function fmt2(n) {
  const v = parseFloat(n);
  return isNaN(v) ? null : v.toFixed(2);
}
function fmtVol(n) {
  const v = parseFloat(n);
  if (isNaN(v) || v === 0) return null;
  if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(0) + 'K';
  return String(Math.round(v));
}
function fmtShares(n) {
  const v = parseFloat(n);
  if (isNaN(v) || v === 0) return null;
  if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  return String(Math.round(v));
}
function fmtFloat(n) {
  // Finnhub shareFloat in millions
  const v = parseFloat(n);
  if (isNaN(v) || v === 0) return null;
  if (v >= 1000) return (v / 1000).toFixed(2) + 'B';
  return v.toFixed(1) + 'M';
}
function returnColor(r) {
  if (r === null || r === undefined) return '#475569';
  return r >= 0 ? '#00ff88' : '#ff4444';
}
function returnLabel(r) {
  if (r === null || r === undefined) return '—';
  return (r >= 0 ? '+' : '') + r.toFixed(1) + '%';
}

const PerfBar = ({ value, max = 200 }) => {
  if (value === null || value === undefined) return (
    <div style={{ height:'3px', background:'rgba(255,255,255,0.04)', borderRadius:'2px',
      border:'1px dashed rgba(255,255,255,0.07)' }} />
  );
  const isPos = value >= 0;
  const pct   = Math.min(Math.abs(value) / max * 100, 100);
  const color = isPos ? '#00ff88' : '#ff4444';
  return (
    <div style={{ position:'relative', height:'3px', background:'rgba(255,255,255,0.06)',
      borderRadius:'2px', overflow:'hidden' }}>
      <div style={{ position:'absolute', [isPos ? 'left' : 'right']:0, top:0,
        height:'100%', width:pct+'%', background:color, borderRadius:'2px',
        transition:'width 0.6s ease' }} />
    </div>
  );
};

export default function HeroPrice({
  priceStr, displayChange, displayChangePct, marketClosed, prevCloseStr, quote,
  memberAnalysis, ticker, onNavigate, token,
}) {
  const isUp      = displayChangePct >= 0;
  const chgColor  = isUp ? '#00ff88' : '#ff4444';
  const chgBg     = isUp ? 'rgba(0,255,136,0.07)' : 'rgba(255,68,68,0.07)';
  const chgBorder = isUp ? 'rgba(0,255,136,0.2)'  : 'rgba(255,68,68,0.2)';

  const dayHigh = fmt2(quote?.high);
  const dayLow  = fmt2(quote?.low);
  const dayOpen = fmt2(quote?.open);
  const px      = parseFloat(quote?.price || 0);
  const hasDayRange = dayHigh && dayLow && parseFloat(dayHigh) > 0 && parseFloat(dayLow) > 0;
  const dayRangePct = hasDayRange
    ? Math.min(98, Math.max(2, Math.round(((px - parseFloat(dayLow)) / (parseFloat(dayHigh) - parseFloat(dayLow))) * 100)))
    : null;
  const volume    = fmtVol(quote?.volume);
  const avgVolume = fmtVol(quote?.avgVolume || quote?.averageVolume);

  // ── Performance lazy fetch ─────────────────────────────────────────────────
  const [perf,        setPerf]        = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfFetched, setPerfFetched] = useState(false);

  useEffect(() => {
    if (!memberAnalysis || !ticker || perfFetched) return;
    let cancelled = false;
    setPerfLoading(true);
    setPerfFetched(true);
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;
    fetch(`${API_URL}/api/stocks/performance/${ticker}`, { headers })
      .then(r => r.json())
      .then(d => {
        if (!cancelled && d.success) {
          setPerf(d.data || null);
          // D3 carry-forward: log performance view event with conviction context
          // Member sees signal + 5Y performance = conviction decision moment → L2 cross-signal
          if (d.data && memberAnalysis) {
            fetch(`${API_URL}/api/chart-signals/log`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
              body: JSON.stringify({
                ticker,
                eventType: 'performance_view',
                signal:     memberAnalysis.recommendation || null,
                score:      memberAnalysis.score          || null,
                fqScore:    memberAnalysis.fqScore        || null,
                return1Y:   d.data.return1Y,
                return3Y:   d.data.return3Y,
                return5Y:   d.data.return5Y,
              }),
            }).catch(() => {});
          }
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPerfLoading(false); });
    return () => { cancelled = true; };
  }, [memberAnalysis, ticker]); // eslint-disable-line

  // ── Share structure ────────────────────────────────────────────────────────
  const md             = memberAnalysis?.marketData || {};
  const sharesDisplay  = fmtShares(md.sharesOutstanding);
  const floatDisplay   = fmtFloat(md.shareFloat);
  const shortRatioRaw  = parseFloat(md.shortRatio);
  const shortRatioDisp = !isNaN(shortRatioRaw) && md.shortRatio !== null ? shortRatioRaw.toFixed(1) + 'd' : null;
  const hasShareStr    = memberAnalysis && (sharesDisplay || floatDisplay || shortRatioDisp);
  const perfMax        = perf
    ? Math.max(100, Math.abs(perf.return5Y || 0), Math.abs(perf.return3Y || 0), Math.abs(perf.return1Y || 0))
    : 200;

  return (
    <div style={{ flex:'1 1 0', padding:'1.4rem 1.5rem', display:'flex',
      flexDirection:'column', justifyContent:'flex-start', gap:'0.55rem', minWidth:0 }}>

      <div style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.1em',
        color:'#475569', textTransform:'uppercase' }}>
        {marketClosed ? 'Last Price' : 'Live Price'}
      </div>

      <div style={{ fontSize:'2.4rem', fontWeight:900, color:'#f8fafc',
        letterSpacing:'-0.03em', lineHeight:1 }}>{priceStr}</div>

      {marketClosed ? (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem' }}>
          <span style={{ fontSize:'0.7rem', fontWeight:700, color:'#64748b',
            background:'rgba(100,116,139,0.1)', border:'1px solid rgba(100,116,139,0.2)',
            borderRadius:'20px', padding:'0.2rem 0.65rem', display:'inline-block', width:'fit-content' }}>
            🔴 Market closed
          </span>
          {prevCloseStr && <span style={{ fontSize:'0.72rem', color:'#475569' }}>Prev close: {prevCloseStr}</span>}
        </div>
      ) : (
        <div style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem',
          padding:'0.25rem 0.75rem', background:chgBg, border:'1px solid '+chgBorder,
          borderRadius:'20px', width:'fit-content' }}>
          <span style={{ fontSize:'0.88rem', fontWeight:800, color:chgColor }}>
            {isUp ? '▲' : '▼'} ${Math.abs(displayChange).toFixed(2)}
          </span>
          <span style={{ fontSize:'0.78rem', fontWeight:700, color:chgColor, opacity:0.85 }}>
            ({Math.abs(displayChangePct).toFixed(2)}%)
          </span>
        </div>
      )}

      {hasDayRange && dayRangePct !== null && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between',
            fontSize:'0.62rem', color:'#475569', marginBottom:'0.28rem', fontWeight:600 }}>
            <span>L ${dayLow}</span>
            <span style={{ color:'#64748b', letterSpacing:'0.04em' }}>DAY RANGE</span>
            <span>H ${dayHigh}</span>
          </div>
          <div style={{ position:'relative', height:'4px', background:'rgba(255,255,255,0.07)', borderRadius:'4px' }}>
            <div style={{ position:'absolute', left:0, top:0, height:'100%',
              width:dayRangePct+'%', background:chgColor, borderRadius:'4px', opacity:0.5 }} />
            <div style={{ position:'absolute', top:'-4px', left:dayRangePct+'%',
              transform:'translateX(-50%)', width:'12px', height:'12px', borderRadius:'50%',
              background:chgColor, border:'2px solid #0a0f1e' }} />
          </div>
          {dayOpen && <div style={{ fontSize:'0.62rem', color:'#334155', marginTop:'0.25rem' }}>Open ${dayOpen}</div>}
        </div>
      )}

      {volume && (
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem',
          padding:'0.3rem 0', borderTop:'1px solid rgba(255,255,255,0.05)' }}>
          <span style={{ fontSize:'0.62rem', fontWeight:700, color:'#334155',
            letterSpacing:'0.06em', textTransform:'uppercase' }}>Vol</span>
          <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#64748b' }}>{volume}</span>
          {avgVolume && (
            <><span style={{ fontSize:'0.62rem', color:'#1e293b' }}>·</span>
            <span style={{ fontSize:'0.62rem', color:'#334155' }}>avg {avgVolume}</span></>
          )}
        </div>
      )}

      {/* ── Share Structure — members post-analysis ── */}
      {hasShareStr && (
        <div style={{ borderTop:'1px solid rgba(255,255,255,0.05)', paddingTop:'0.45rem' }}>
          <div style={{ fontSize:'0.58rem', fontWeight:800, color:'#334155',
            letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'0.35rem' }}>
            Share Structure
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.18rem' }}>
            {sharesDisplay && (
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:'0.65rem', color:'#475569' }}>Outstanding</span>
                <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#94a3b8' }}>{sharesDisplay}</span>
              </div>
            )}
            {floatDisplay && (
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:'0.65rem', color:'#475569' }}>Float</span>
                <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#94a3b8' }}>{floatDisplay}</span>
              </div>
            )}
            {shortRatioDisp && (
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:'0.65rem', color:'#475569' }}>Short Ratio</span>
                <span style={{ fontSize:'0.72rem', fontWeight:700,
                  color: shortRatioRaw > 5 ? '#ff4444' : shortRatioRaw > 2 ? '#ffaa00' : '#64748b' }}>
                  {shortRatioDisp}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Price Performance — members post-analysis, lazy loaded ── */}
      {memberAnalysis && (
        <div style={{ borderTop:'1px solid rgba(255,255,255,0.05)', paddingTop:'0.45rem' }}>
          <div style={{ fontSize:'0.58rem', fontWeight:800, color:'#334155',
            letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'0.35rem' }}>
            Price Performance
          </div>
          {perfLoading ? (
            <div style={{ fontSize:'0.65rem', color:'#334155', fontStyle:'italic' }}>Loading…</div>
          ) : perf ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.32rem' }}>
              {[
                { label:'1 Year',  value: perf.return1Y },
                { label:'3 Year',  value: perf.return3Y },
                { label:'5 Year',  value: perf.return5Y },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ display:'flex', justifyContent:'space-between',
                    alignItems:'center', marginBottom:'0.12rem' }}>
                    <span style={{ fontSize:'0.65rem', color:'#475569' }}>{label}</span>
                    <span style={{ fontSize:'0.72rem', fontWeight:800, color:returnColor(value) }}>
                      {returnLabel(value)}
                    </span>
                  </div>
                  <PerfBar value={value} max={perfMax} />
                </div>
              ))}
              <div style={{ fontSize:'0.56rem', color:'#1e293b', marginTop:'0.1rem' }}>
                Monthly adjusted · {perf.asOf}
              </div>
            </div>
          ) : !perfLoading ? (
            <div style={{ fontSize:'0.65rem', color:'#334155' }}>Unavailable</div>
          ) : null}

          {onNavigate && (
            <button onClick={() => onNavigate('calculator', {
              sourceTicker: ticker,
              signal: memberAnalysis?.recommendation || null,
              score:  memberAnalysis?.score          || null,
            })}
              style={{ marginTop:'0.45rem', width:'100%', padding:'0.3rem 0.5rem',
                background:'rgba(0,212,255,0.05)', border:'1px solid rgba(0,212,255,0.12)',
                borderRadius:'6px', color:'#00d4ff', fontSize:'0.65rem', fontWeight:700,
                cursor:'pointer', fontFamily:'inherit', textAlign:'center' }}>
              📊 Compare tickers → Calculator
            </button>
          )}
        </div>
      )}

      <div style={{ marginTop:'auto', paddingTop:'0.4rem',
        fontSize:'0.62rem', color:'#1e293b', letterSpacing:'0.03em' }}>
        ⏱ 15-min delayed · Not real-time
      </div>
    </div>
  );
}
